import React, { useRef, useState } from 'react';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';

/**
 * Photo Gallery component for profile photos
 * Design: High-contrast, grid layout with drag-and-drop
 * Max 6 photos, drag to reorder
 */
export default function PhotoGallery({
  photos = [],
  onUpload,
  onDelete,
  onReorder,
  maxPhotos = 6,
  isLoading = false,
  className = '',
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    setUploadError(null);

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    try {
      await onUpload?.(file);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload photo');
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
    // Reset input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  // Drag and drop reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handlePhotoDropOn = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newOrder = [...photos.map(p => p.id)];
    const [movedId] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, movedId);

    onReorder?.(newOrder);
    setDraggedIndex(null);
  };

  const canAddMore = photos.length < maxPhotos;

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="sr-only"
      />

      {/* Error display */}
      {uploadError && (
        <div className="mb-4 p-3 border-2 border-red-500 bg-red-50">
          <p className="font-mono text-sm text-red-600">{uploadError}</p>
        </div>
      )}

      {/* Photo grid */}
      <div className="grid grid-cols-3 gap-3">
        {/* Existing photos */}
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handlePhotoDropOn(e, index)}
            className={`
              relative aspect-square border-2 border-black
              cursor-move group
              ${draggedIndex === index ? 'opacity-50' : ''}
            `}
          >
            <img
              src={photo.url}
              alt={photo.caption || `Gallery photo ${index + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Order indicator */}
            <div className="absolute top-1 left-1 w-6 h-6 bg-black text-white flex items-center justify-center">
              <span className="font-mono text-xs font-bold">{index + 1}</span>
            </div>

            {/* Delete button (shown on hover) */}
            <button
              type="button"
              onClick={() => onDelete?.(photo.id)}
              className="
                absolute top-1 right-1
                w-6 h-6 bg-red-500 text-white
                flex items-center justify-center
                opacity-0 group-hover:opacity-100
                transition-opacity duration-200
                hover:bg-red-600
              "
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="square" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Caption overlay (if exists) */}
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/70">
                <p className="font-mono text-xs text-white truncate">{photo.caption}</p>
              </div>
            )}
          </div>
        ))}

        {/* Upload slot */}
        {canAddMore && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`
              aspect-square
              border-2 border-dashed
              flex flex-col items-center justify-center
              cursor-pointer
              transition-colors duration-200
              ${isDragging
                ? 'border-black bg-gray-100'
                : 'border-gray-400 bg-white hover:border-black hover:bg-gray-50'
              }
              ${isLoading ? 'pointer-events-none' : ''}
            `}
          >
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <div className="w-8 h-8 border-2 border-current flex items-center justify-center text-gray-400 group-hover:text-black mb-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="square" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                  ADD PHOTO
                </p>
                <p className="font-mono text-xs text-gray-400 mt-1">
                  {photos.length}/{maxPhotos}
                </p>
              </>
            )}
          </div>
        )}

        {/* Empty slots */}
        {Array.from({ length: Math.max(0, maxPhotos - photos.length - 1) }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="aspect-square border-2 border-dashed border-gray-200 bg-gray-50"
          />
        ))}
      </div>

      {/* Instructions */}
      <p className="font-mono text-xs text-gray-500 mt-3 uppercase tracking-wider">
        Drag photos to reorder. First photo is your primary.
      </p>
    </div>
  );
}
